<!-- Overlay backdrop -->
<div *ngIf="visible"
     class="fixed inset-0 bg-black/50 z-[9999] flex items-start sm:items-center justify-center pt-16 sm:pt-4 px-4 pb-4 overflow-y-auto"
     (click)="closeDashboard()">

  <!-- Dashboard panel -->
  <div class="bg-gray-900 text-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] sm:max-h-[90vh] overflow-hidden flex flex-col my-auto"
       (click)="$event.stopPropagation()">

    <!-- Header -->
    <div class="bg-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-700">
      <div class="flex items-center gap-2">
        <span class="text-xl">🎯</span>
        <h2 class="text-lg font-semibold">Worker Dashboard</h2>
      </div>
      <button (click)="closeDashboard()"
              class="text-gray-400 hover:text-white transition-colors">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Content -->
    <div class="overflow-y-auto flex-1 p-4 space-y-4">

      <!-- WebSocket Worker Status -->
      <div class="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 class="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <span>🔌</span>
          <span>WebSocket Worker</span>
        </h3>

        <div *ngIf="websocketWorker; else workerLoading" class="space-y-3">
          <!-- Status -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">Status</span>
            <div class="flex items-center gap-2">
              <span class="text-xl">{{ getStatusIcon(websocketWorker.status) }}</span>
              <span class="text-sm font-medium" [ngClass]="getStatusColor(websocketWorker.status)">
                {{ websocketWorker.status }}
              </span>
            </div>
          </div>

          <!-- Health -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">Health</span>
            <span class="text-sm" [ngClass]="websocketWorker.healthy ? 'text-green-400' : 'text-red-400'">
              {{ websocketWorker.healthy ? '✅ Healthy' : '❌ Unhealthy' }}
            </span>
          </div>

          <!-- Connection Type -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">Type</span>
            <span class="text-sm text-brand-400">{{ websocketWorker.type }}</span>
          </div>

          <!-- State -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">Connection State</span>
            <span class="text-sm text-purple-400">{{ websocketWorker.state }}</span>
          </div>

          <!-- Uptime -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">Uptime</span>
            <span class="text-sm text-gray-300">{{ formatUptime(websocketWorker.uptime) }}</span>
          </div>

          <!-- Last Updated -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">Last Updated</span>
            <span class="text-sm text-gray-300">{{ websocketWorker.timestamp | date:'HH:mm:ss' }}</span>
          </div>
        </div>

        <ng-template #workerLoading>
          <div class="text-sm text-gray-400 text-center py-2">Loading worker status...</div>
        </ng-template>
      </div>

      <!-- Connection Stats -->
      <div class="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 class="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <span>📡</span>
          <span>Connection Stats</span>
        </h3>

        <div *ngIf="connectionStats; else statsLoading" class="space-y-3">
          <!-- State -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">State</span>
            <span class="text-sm text-brand-400">{{ connectionStats.state }}</span>
          </div>

          <!-- Connected -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">Connected</span>
            <span class="text-sm" [ngClass]="connectionStats.connected ? 'text-green-400' : 'text-gray-500'">
              {{ connectionStats.connected ? '✅ Yes' : '❌ No' }}
            </span>
          </div>

          <!-- User Email -->
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-400">User Email</span>
            <span class="text-xs text-gray-300 font-mono truncate max-w-[200px]" [title]="connectionStats.userEmail">
              {{ connectionStats.userEmail || 'N/A' }}
            </span>
          </div>
        </div>

        <ng-template #statsLoading>
          <div class="text-sm text-gray-400 text-center py-2">Loading connection stats...</div>
        </ng-template>
      </div>

      <!-- Test Actions -->
      <div class="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 class="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <span>🧪</span>
          <span>Test Actions</span>
        </h3>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <!-- Refresh Dashboard -->
          <button (click)="refreshDashboard()"
                  class="bg-brand hover:bg-brand-600 text-white px-3 py-2 rounded-btn text-sm font-medium transition-colors">
            Refresh
          </button>

          <!-- Export State -->
          <button (click)="exportState()"
                  class="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
            💾 Export State
          </button>

          <!-- Test Worker Failover (disabled for now) -->
          <button (click)="testWorkerFailover()"
                  class="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors opacity-50 cursor-not-allowed"
                  disabled>
            ⚡ Test Failover
          </button>
        </div>
      </div>

    </div>

    <!-- Footer -->
    <div class="bg-gray-800 px-4 py-2 border-t border-gray-700 text-xs text-gray-400 text-center">
      Updates every 100ms • Mobile-optimized for Android testing
    </div>

  </div>
</div>
